import React, { useState } from "react";

const BODY_ZONES = {
  head: { x: 200, y: 45, label: "Head Position", tip: "Keep chin tucked, head behind shoulder when punching" },
  left_shoulder: { x: 145, y: 110, label: "Left Shoulder", tip: "Rotate shoulder into jab for power and reach" },
  right_shoulder: { x: 255, y: 110, label: "Right Shoulder", tip: "Right shoulder should protect chin when not punching" },
  left_elbow: { x: 115, y: 175, label: "Left Elbow", tip: "Elbow tucked to protect body on left side" },
  right_elbow: { x: 285, y: 175, label: "Right Elbow", tip: "Rear elbow guards body, flaring exposes ribs" },
  left_hand: { x: 90, y: 235, label: "Left Guard", tip: "Lead hand at cheekbone height, ready to jab" },
  right_hand: { x: 310, y: 235, label: "Right Guard", tip: "Rear hand glued to chin at all times" },
  core: { x: 200, y: 255, label: "Core & Hips", tip: "Hip rotation generates power in all punches" },
  left_knee: { x: 160, y: 355, label: "Left Knee", tip: "Slight bend in knees for mobility and balance" },
  right_knee: { x: 240, y: 355, label: "Right Knee", tip: "Push off rear knee to drive power forward" },
  left_foot: { x: 150, y: 440, label: "Left Foot", tip: "Lead foot points toward opponent, shoulder width apart" },
  right_foot: { x: 250, y: 440, label: "Right Foot", tip: "Rear heel raised, weight on ball of foot for mobility" },
};

const STATUS_COLORS = {
  good: { fill: "rgba(76, 175, 80, 0.3)", stroke: "#4caf50", glow: "rgba(76, 175, 80, 0.6)" },
  warning: { fill: "rgba(255, 152, 0, 0.3)", stroke: "#ff9800", glow: "rgba(255, 152, 0, 0.6)" },
  issue: { fill: "rgba(233, 69, 96, 0.3)", stroke: "#e94560", glow: "rgba(233, 69, 96, 0.6)" },
  neutral: { fill: "rgba(255, 255, 255, 0.05)", stroke: "rgba(255,255,255,0.2)", glow: "transparent" },
};

function FighterSilhouette({ zoneStatuses, onZoneClick, activeZone }) {
  return (
    <svg viewBox="0 0 400 480" style={{width:"100%",maxWidth:"300px"}}>
      <defs>
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <filter key={status} id={"glow-" + status}>
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        ))}
      </defs>

      <g opacity="0.15" fill="white">
        <circle cx="200" cy="45" r="28"/>
        <rect x="160" y="73" width="80" height="15" rx="7"/>
        <rect x="148" y="88" width="104" height="120" rx="20"/>
        <rect x="100" y="95" width="28" height="90" rx="14"/>
        <rect x="272" y="95" width="28" height="90" rx="14"/>
        <rect x="88" y="183" width="24" height="70" rx="12"/>
        <rect x="288" y="183" width="24" height="70" rx="12"/>
        <rect x="163" y="208" width="74" height="110" rx="15"/>
        <rect x="158" y="318" width="34" height="100" rx="17"/>
        <rect x="208" y="318" width="34" height="100" rx="17"/>
        <rect x="148" y="415" width="50" height="25" rx="10"/>
        <rect x="202" y="415" width="50" height="25" rx="10"/>
      </g>

      {Object.entries(BODY_ZONES).map(([zone, pos]) => {
        const status = zoneStatuses?.[zone] || "neutral";
        const colors = STATUS_COLORS[status];
        const isActive = activeZone === zone;
        const hasIssue = status !== "neutral";

        return (
          <g key={zone} onClick={() => onZoneClick(zone)} style={{cursor:"pointer"}}>
            <circle
              cx={pos.x} cy={pos.y} r={isActive ? 18 : 14}
              fill={colors.fill}
              stroke={colors.stroke}
              strokeWidth={isActive ? 3 : 2}
              filter={hasIssue ? "url(#glow-" + status + ")" : undefined}
              style={{transition:"all 0.3s ease"}}
            />
            {hasIssue && (
              <circle cx={pos.x} cy={pos.y} r="6" fill={colors.stroke} opacity="0.9"/>
            )}
            {status === "neutral" && (
              <circle cx={pos.x} cy={pos.y} r="4" fill="rgba(255,255,255,0.3)"/>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function BodyFeedback({ analysis }) {
  const [activeZone, setActiveZone] = useState(null);

  if (!analysis) return null;

  const zoneStatuses = analysis.zones || {};
  const activeZoneData = activeZone ? BODY_ZONES[activeZone] : null;
  const activeStatus = activeZone ? (zoneStatuses[activeZone] || "neutral") : null;
  const activeFeedback = activeZone ? (analysis.zoneFeedback?.[activeZone] || activeZoneData?.tip) : null;

  const issueCount = Object.values(zoneStatuses).filter(s => s === "issue").length;
  const warningCount = Object.values(zoneStatuses).filter(s => s === "warning").length;
  const goodCount = Object.values(zoneStatuses).filter(s => s === "good").length;

  return (
    <div style={{fontFamily:"Inter, sans-serif"}}>
      <div style={{display:"flex",gap:"15px",marginBottom:"20px",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
          <div style={{width:"10px",height:"10px",borderRadius:"50%",background:"#e94560"}}></div>
          <span style={{color:"#888",fontSize:"0.8rem"}}>{issueCount} issues</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
          <div style={{width:"10px",height:"10px",borderRadius:"50%",background:"#ff9800"}}></div>
          <span style={{color:"#888",fontSize:"0.8rem"}}>{warningCount} warnings</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
          <div style={{width:"10px",height:"10px",borderRadius:"50%",background:"#4caf50"}}></div>
          <span style={{color:"#888",fontSize:"0.8rem"}}>{goodCount} good</span>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"20px",alignItems:"start"}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
          <p style={{color:"#444",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"10px"}}>Tap a zone for feedback</p>
          <FighterSilhouette
            zoneStatuses={zoneStatuses}
            onZoneClick={setActiveZone}
            activeZone={activeZone}
          />
        </div>

        <div>
          {activeZone ? (
            <div style={{background: activeStatus === "issue" ? "rgba(233,69,96,0.08)" : activeStatus === "warning" ? "rgba(255,152,0,0.08)" : activeStatus === "good" ? "rgba(76,175,80,0.08)" : "rgba(255,255,255,0.03)",border: "1px solid " + (activeStatus === "issue" ? "rgba(233,69,96,0.3)" : activeStatus === "warning" ? "rgba(255,152,0,0.3)" : activeStatus === "good" ? "rgba(76,175,80,0.3)" : "rgba(255,255,255,0.08)"),borderRadius:"14px",padding:"20px"}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
                <div style={{width:"8px",height:"8px",borderRadius:"50%",background: activeStatus === "issue" ? "#e94560" : activeStatus === "warning" ? "#ff9800" : activeStatus === "good" ? "#4caf50" : "#555",flexShrink:0}}></div>
                <p style={{color:"white",margin:0,fontWeight:"700",fontSize:"0.95rem"}}>{BODY_ZONES[activeZone].label}</p>
              </div>
              <p style={{color:"#ccc",margin:0,lineHeight:"1.7",fontSize:"0.9rem"}}>{activeFeedback}</p>
            </div>
          ) : (
            <div>
              <p style={{color:"#555",fontSize:"0.85rem",marginBottom:"15px"}}>Zones detected:</p>
              {Object.entries(zoneStatuses).filter(([_, s]) => s !== "neutral").map(([zone, status]) => (
                <div key={zone} onClick={() => setActiveZone(zone)} style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 14px",borderRadius:"10px",marginBottom:"8px",cursor:"pointer",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)"}}>
                  <div style={{width:"8px",height:"8px",borderRadius:"50%",background: status === "issue" ? "#e94560" : status === "warning" ? "#ff9800" : "#4caf50",flexShrink:0}}></div>
                  <p style={{color:"white",margin:0,fontSize:"0.85rem"}}>{BODY_ZONES[zone].label}</p>
                  <span style={{color: status === "issue" ? "#e94560" : status === "warning" ? "#ff9800" : "#4caf50",fontSize:"0.75rem",marginLeft:"auto",textTransform:"uppercase",letterSpacing:"0.5px"}}>{status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { BodyFeedback, BODY_ZONES };
export default BodyFeedback;
